import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Shield, MoreHorizontal, Pen, Eye, UserCheck, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { cn } from '../lib/utils';
import { motion, useAnimation } from "framer-motion";
import { toast } from 'react-hot-toast';
interface Job {
  id: string;
  companyName: string;
  hrName: string;
  hrEmail: string;
  hrContact: string;
  isApproved: boolean;
  isjobDetailsAdded: boolean;
  jobTitle: string;
  jobCategory: string;
  shiftTimings: string;
  employmentType: string;
  workType: string;
  jobDescription: string;
  streetAddress: string;
  area: string;
  city: string;
  pincode: string;
  locationLink: string;
  minSalary: number;
  maxSalary: number;
  skills: string[];
  spokenLanguages: string[];
  noOfOpenings: number;
  hiringNeed: string;
  minQualification: string;
  totalExperience: number;
  createdAt: string;
}
interface CompanyTableProps {
  jobs: Job[];
  setJobs: (jobs: Job[]) => void;
  isloading: boolean;
  onJobSelect: (job: Job) => void;
  expandedRows: string[];
  setExpandedRows: (rows: string[]) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (column: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onStatusToggle: (job: Job) => void;
  statusLoading: string | null;
}

const CompanyTable: React.FC<CompanyTableProps> = ({
  jobs,
  setJobs,
  isloading,
  onJobSelect,
  expandedRows,
  setExpandedRows,
  sortBy,
  sortOrder,
  onSort,
  currentPage,
  totalPages,
  onPageChange,
  onStatusToggle,
  statusLoading
}) => {
  const [actionJob, setActionJob] = useState<Job | null>(null);
  const [statusConfirmJob, setStatusConfirmJob] = useState<Job | null>(null);
  const [localJobs, setLocalJobs] = useState(jobs);
  const controls = useAnimation();

  // Sync localJobs state with jobs prop
  useEffect(() => {
    setLocalJobs(jobs);
  }, [jobs]);

  // Log the jobs array to check data
  useEffect(() => {
    console.log('Jobs array:', jobs);
  }, [jobs]);

  const getCategoryBadgeColor = (category: string) => {
    // You can customize based on categories
    switch (category) {
      case 'Aviation': return 'bg-blue-100 text-blue-800 border-blue-200 hover:border-blue-800 hover:bg-white';
      case 'Banking': return 'bg-purple-100 text-purple-800 border-purple-200 hover:border-purple-800 hover:bg-white';
      default: return 'bg-gray-100 text-gray-800 border-gray-200 hover:border-gray-800 hover:bg-white';
    }
  };

  const handleStatusToggle = (job: Job) => {
    setStatusConfirmJob(job);
  };

  const confirmStatusToggle = () => {
    if (statusConfirmJob) {
      onStatusToggle(statusConfirmJob);
      setStatusConfirmJob(null);
    }
  };

  const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
    column,
    children,
    className
  }) => (
    <th
      className={cn(
        "px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#F5F1F9] z-10",
        className
      )}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column && (
          sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );



  return (
    <div className="bg-background relative">
      <div className="max-h-[calc(100vh-200px)]" style={{ maxWidth: '100vw' }}>
        <table className="min-w-full divide-y divide-border">
          <thead style={{ backgroundColor: '#FFF5F2' }}>
            <tr>
              <th className="px-4 py-3 w-12 sticky top-0 bg-[#FFF5F2] z-10"></th>
              <SortableHeader column="companyName" className="min-w-48 sticky left-0 bg-[#FFF5F2] z-20 border-r border-border">
                Company Name
              </SortableHeader>
              <SortableHeader column="hrName" className="bg-[#FFF5F2] min-w-48">HR Name</SortableHeader>
              <SortableHeader column="jobCategory" className="bg-[#FFF5F2] min-w-32">Category</SortableHeader>
              <SortableHeader column="city" className="bg-[#FFF5F2] min-w-32">Location</SortableHeader>
              <SortableHeader column="createdAt" className="bg-[#FFF5F2] min-w-40">Created At</SortableHeader>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-border">
            {isloading ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <svg
                      className="animate-spin h-5 w-5 text-orange-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                    Loading...
                  </div>
                </td>
              </tr>
            ) : localJobs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <p className="text-muted-foreground">No jobs found matching your criteria.</p>
                </td>
              </tr>
            ) : (
              localJobs.map((job) => (
                <React.Fragment key={job.id}>
                  <tr
                    className={cn(
                      "hover:bg-muted/30 transition-colors cursor-pointer"
                    )}
                    onClick={() => onJobSelect(job)}
                  >
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      {/* Checkbox or expand if needed */}
                    </td>
                    <td
                      className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border relative group"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`font-medium transition-colors cursor-pointer ${job.isApproved ? 'text-foreground hover:text-primary' : 'text-[#F28B82] hover:text-[#E6492D]'}`}>
                          {job.companyName}
                        </span>
                        {!job.isApproved && (
                          <Badge variant="destructive" className="text-xs ml-2">
                            Pending
                          </Badge>
                        )}
                      </div>
                      <div className="absolute top-0 left-[calc(100%+0.5rem)] mt-1 w-80 p-4 bg-white shadow-lg rounded border border-border opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto z-30">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-lg">{job.companyName}</h4>
                          {!job.isApproved && (
                            <Badge variant="destructive" className="text-xs ml-2">
                              Pending
                            </Badge>
                          )}
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">HR Name:</span>
                            <span>{job.hrName}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Category:</span>
                            <Badge variant="secondary" className="text-xs">
                              {job.jobCategory}
                            </Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Location:</span>
                            <div className="max-w-48 truncate" title={job.city}>
                              {job.city}
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 border-t border-border">
                          <div className="text-xs text-muted-foreground">
                            Created {new Date(job.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      <span className="font-medium">
                        {job.hrName}
                      </span>
                    </td>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      <Badge className={getCategoryBadgeColor(job.jobCategory)}>
                        {job.jobCategory}
                      </Badge>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      <div className="max-w-48 truncate" title={job.city}>
                        {job.city}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-muted-foreground">
                      {new Date(job.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                      {job.isApproved ? (
                        <span className="text-sm text-green-600 font-medium">Approved</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Button 
                            onClick={() => handleStatusToggle(job)}
                            disabled={statusLoading === job.id}
                            className="bg-orange-600 hover:bg-orange-700 text-white"
                          >
                            <Shield className="mr-2 h-4 w-4" />
                            {statusLoading === job.id ? 'Approving...' : 'Approve'}
                          </Button>
                          {!job.isjobDetailsAdded && (
                            <div className="relative group">
                              <Info className="h-4 w-4 text-orange-600 cursor-help" />
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50">
                                Company details and job post not added
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Approve/Disapprove Confirmation Dialog */}
      <AlertDialog open={!!statusConfirmJob} onOpenChange={() => setStatusConfirmJob(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusConfirmJob?.isApproved ? 'Disapprove Job' : 'Approve Job'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to {statusConfirmJob?.isApproved ? 'disapprove' : 'approve'} the job{' '}
              <strong>{statusConfirmJob?.jobTitle}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmStatusToggle}
              className={statusConfirmJob?.isApproved ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
            >
              {statusConfirmJob?.isApproved ? 'Disapprove Job' : 'Approve Job'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompanyTable;